import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { workflowProvider } from "@gtm/contracts/orchestration";
import type { ReactNode } from "react";
import { useState, useTransition } from "react";
import { useRouter, useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { RecordTable } from "@/components/workspace/record-table";
import { Status } from "@/components/workspace/status";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { WorkflowLabel } from "./workflow-label";

/**
 * Rows are Temporal executions keyed by workflow ID. Retry starts a brand-new
 * run rather than resuming this one, so the bulk surface offers cancel only,
 * and only executions Temporal still reports running are selectable.
 */
const CANCELLABLE = ["running"];
export type RunHistoryRow = {
  id: string;
  workflow: string;
  workflowKey: string;
  repository: string;
  status: string;
  requested: string;
};
export function RunHistory({
  rows,
  loading,
  error,
  onRetry,
  page,
  hasNext,
  total,
  query,
}: {
  rows: RunHistoryRow[];
  loading?: boolean;
  error?: Error;
  onRetry?: () => void;
  page: number;
  hasNext: boolean;
  total: number;
  query: Record<string, string | undefined>;
}) {
  const router = useRouter();
  const { update } = useUrlState();
  const [busy, startNavigation] = useTransition();
  const [pending, setPending] = useState(false);
  const { toast } = useToast();
  const statuses = new Map(rows.map((r) => [r.id, r.status] as const));
  async function cancelRuns(ids: string[], clear: () => void) {
    setPending(true);
    try {
      const result = await unwrap(
        apiClient().POST("/workflows/runs/actions", {
          body: { workflowIds: ids, action: "cancel" },
        }),
      );
      if (result.succeeded.length) clear();
      toast({
        variant: result.failed.length ? ToastVariant.WARNING : ToastVariant.SUCCESS,
        header: `Cancellation requested for ${result.succeeded.length} of ${ids.length} runs`,
        description: result.failed.length ? result.failed[0].error : "The workflows stop shortly.",
        ...toastDefaults(result.failed.length ? ToastVariant.WARNING : ToastVariant.SUCCESS),
      });
      router.refresh();
    } catch (e) {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not cancel runs",
        description: (e as Error).message,
        ...toastDefaults(ToastVariant.ERROR),
      });
    } finally {
      setPending(false);
    }
  }
  const columns = [
    { key: "workflow", label: "Workflow", width: 280, minWidth: 280 },
    { key: "repository", label: "Source", width: 240 },
    { key: "status", label: "Status", width: "auto" as const },
    { key: "requested", label: "Requested", width: 200 },
  ];
  function go(next: number) {
    const params = new URLSearchParams(
      Object.entries(query).filter((entry): entry is [string, string] => !!entry[1]),
    );
    params.delete("runId");
    if (next > 1) params.set("page", String(next));
    else params.delete("page");
    startNavigation(() => router.push(`/workflows?${params}`));
  }
  return (
    <RecordTable
      isLoading={busy || loading}
      error={error}
      onRetry={onRetry}
      label="Runs"
      detailParam="runId"
      rowKeys={rows.map((r) => r.id)}
      columns={columns}
      bulk={{
        isSelectable: (id) => CANCELLABLE.includes(statuses.get(id) || ""),
        actions: (selected, clear) => (
          <>
            <Button
              label={`Cancel selected${selected.length ? ` (${selected.length})` : ""}`}
              isDisabled={pending || !selected.length}
              onClick={() => cancelRuns(selected, clear)}
            />
            <Button
              label="Clear selection"
              variant={ButtonVariant.TERTIARY}
              isDisabled={!selected.length}
              onClick={clear}
            />
          </>
        ),
      }}
      onRowClick={(id) =>
        update({
          runId: id,
          activityId: null,
          personId: null,
          person: null,
          personTab: null,
        })
      }
      rows={rows.map(
        (r): Record<string, ReactNode> => ({
          ...r,
          workflow: (
            <WorkflowLabel source={workflowProvider(r.workflowKey) ?? ""} name={r.workflow} />
          ),
          status: <Status value={r.status} />,
        }),
      )}
      paging={{
        page,
        total,
        hasNext,
        hasPrevious: page > 1,
        busy,
        onNext: () => go(page + 1),
        onPrevious: () => go(page - 1),
      }}
    />
  );
}
